import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  errorCodeLabel,
  failureStageLabel,
  failureStatusLabel,
  failureStatusValues,
} from "../entities/mailboxes";
import { formatDate } from "./counterpartiesSupport";
import { FailureStatusBadge, ImportFailureCard } from "./ImportFailureCard";
import { MailboxNotice } from "./MailboxNotice";
import {
  failuresQuery,
  mailboxKeys,
  mailboxLabel,
  type MailboxesSearch,
  type OrganizationName,
  type RetryableFilter,
} from "./mailboxesSupport";
import type { ShellServices } from "./services";
import { Select } from "./Select";

type Service = NonNullable<ShellServices["mailboxes"]>;

const stageOptions = ["discovery", "import"] as const;

const retryableOptions: readonly { value: RetryableFilter; label: string }[] = [
  { value: "all", label: "Любые" },
  { value: "yes", label: "Можно повторить" },
  { value: "no", label: "Повтор недоступен" },
];

/** Журнал ошибок импорта: фильтры, список и карточка с повтором и разбором. */
export function ImportFailuresPanel({
  service,
  canViewMailboxes,
  organizationName,
  search,
  onSearch,
}: {
  service: Service;
  /** Список ящиков доступен только с правом настройки ящиков. */
  canViewMailboxes: boolean;
  organizationName: OrganizationName;
  search: MailboxesSearch;
  onSearch: (patch: Partial<MailboxesSearch>) => void;
}) {
  const query = useMemo(() => failuresQuery(search), [search]);
  const failures = useQuery({
    queryKey: mailboxKeys.failureList(query),
    queryFn: () => service.failures(query),
    retry: false,
  });
  const mailboxes = useQuery({
    queryKey: mailboxKeys.list("all"),
    queryFn: () => service.list(),
    enabled: canViewMailboxes,
    retry: false,
  });
  const mailboxNames = useMemo(
    () =>
      new Map(
        (mailboxes.data?.data ?? []).map((item) => [
          item.id,
          mailboxLabel(item, organizationName),
        ]),
      ),
    [mailboxes.data, organizationName],
  );
  const items = failures.data?.data ?? [];
  const hasFilters =
    search.status !== "" ||
    search.stage !== "" ||
    search.retryable !== "all" ||
    search.mailbox !== "";

  const opened = search.failure;

  return (
    <div
      className="cp-layout"
      data-detail={opened === undefined ? "closed" : "open"}
    >
      <div className="cp-list-pane">
        <div className="mb-filters" role="group" aria-label="Фильтры ошибок">
          <label className="field cp-field">
            <span>Состояние</span>
            <Select
              value={search.status}
              onChange={(event) =>
                onSearch({ status: event.target.value, failure: undefined })
              }
            >
              <option value="">Все</option>
              {failureStatusValues.map((value) => (
                <option key={value} value={value}>
                  {failureStatusLabel(value)}
                </option>
              ))}
            </Select>
          </label>
          <label className="field cp-field">
            <span>Повтор</span>
            <Select
              value={search.retryable}
              onChange={(event) =>
                onSearch({
                  retryable: event.target.value as RetryableFilter,
                  failure: undefined,
                })
              }
            >
              {retryableOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </label>
          <label className="field cp-field">
            <span>Этап</span>
            <Select
              value={search.stage}
              onChange={(event) =>
                onSearch({ stage: event.target.value, failure: undefined })
              }
            >
              <option value="">Все</option>
              {stageOptions.map((value) => (
                <option key={value} value={value}>
                  {failureStageLabel(value)}
                </option>
              ))}
            </Select>
          </label>
          {canViewMailboxes && (mailboxes.data?.data.length ?? 0) > 0 ? (
            <label className="field cp-field">
              <span>Ящик</span>
              <Select
                value={search.mailbox}
                onChange={(event) =>
                  onSearch({ mailbox: event.target.value, failure: undefined })
                }
              >
                <option value="">Все ящики</option>
                {mailboxes.data?.data.map((item) => (
                  <option key={item.id} value={item.id}>
                    {mailboxLabel(item, organizationName)}
                  </option>
                ))}
              </Select>
            </label>
          ) : null}
          {hasFilters ? (
            <button
              type="button"
              className="button button--text button--compact"
              onClick={() =>
                onSearch({
                  status: "",
                  stage: "",
                  retryable: "all",
                  mailbox: "",
                  failure: undefined,
                })
              }
            >
              Сбросить фильтры
            </button>
          ) : null}
        </div>

        {failures.isLoading ? <p aria-busy="true">Загружаем ошибки…</p> : null}
        {failures.isError ? (
          <div className="cp-empty">
            <MailboxNotice
              error={failures.error}
              fallback="Не удалось загрузить ошибки импорта."
              onRetry={() => void failures.refetch()}
              retrying={failures.isFetching}
            />
          </div>
        ) : null}
        {failures.isSuccess && items.length === 0 ? (
          <div className="cp-empty">
            <p>
              {hasFilters
                ? "По этим фильтрам ошибок нет."
                : "Ошибок импорта нет: письма загружаются без сбоев."}
            </p>
          </div>
        ) : null}

        {items.length > 0 ? (
          <>
            <ul className="cp-list" aria-label="Ошибки импорта">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className="cp-list__item"
                    aria-current={item.id === opened ? "true" : undefined}
                    onClick={() => onSearch({ failure: item.id })}
                  >
                    <span className="cp-list__title">
                      {errorCodeLabel(item.error_code)}
                    </span>
                    <span className="muted">
                      {[
                        item.mailbox_id === null
                          ? null
                          : (mailboxNames.get(item.mailbox_id) ?? null),
                        failureStageLabel(item.stage),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                    <span className="cp-list__meta muted">
                      <FailureStatusBadge status={item.status} />
                      <span>
                        {item.retryable && item.status !== "resolved"
                          ? "можно повторить"
                          : null}
                      </span>
                      <span>{formatDate(item.updated_at)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <p className="muted cp-count">
              Показано {items.length}
              {items.length >= 100
                ? ". Core отдаёт последние 100 ошибок — сузьте фильтры, чтобы увидеть остальные"
                : ""}
            </p>
          </>
        ) : null}
      </div>

      <div className="cp-detail">
        {opened === undefined ? (
          <div className="cp-empty cp-empty--detail">
            <p>
              Выберите ошибку в списке, чтобы увидеть подробности и повторить
              загрузку письма или отметить ошибку разобранной.
            </p>
          </div>
        ) : (
          <ImportFailureCard
            key={opened}
            service={service}
            id={opened}
            mailboxNames={mailboxNames}
            onBack={() => onSearch({ failure: undefined })}
          />
        )}
      </div>
    </div>
  );
}
